import { Icon } from "@iconify/react/dist/iconify.js";
import { type ReactNode, useState } from "react";

type NotificationBannerVariant = "info" | "warning" | "success" | "error";

type NotificationBannerProps = {
  variant?: NotificationBannerVariant;
  icon?: string;
  title: string;
  description?: string | ReactNode;
  className?: string;
  showIcon?: boolean;
};

const variantStyles = {
  info: {
    container: "bg-blue-100 border-blue-200",
    icon: "text-blue-600",
    title: "text-blue-800",
    description: "text-blue-600",
  },
  warning: {
    container: "bg-orange-100 border-orange-200",
    icon: "text-orange-600",
    title: "text-orange-800",
    description: "text-orange-600",
  },
  success: {
    container: "bg-green-50 border-green-200",
    icon: "text-green-600",
    title: "text-green-800",
    description: "text-green-600",
  },
  error: {
    container: "bg-red-50 border-red-200",
    icon: "text-red-600",
    title: "text-red-800",
    description: "text-red-600",
  },
};

const defaultIcons = {
  info: "material-symbols:info-outline",
  warning: "material-symbols:warning-outline",
  success: "material-symbols:check-circle-outline",
  error: "material-symbols:error-outline",
};

export function NotificationBanner({
  variant = "info",
  icon,
  title,
  description,
  className = "",
  showIcon = true,
}: NotificationBannerProps) {
  const styles = variantStyles[variant];
  const iconName = icon || defaultIcons[variant];
  const [hide, setHide] = useState(false);

  return hide ? null : (
    <div className={`border rounded-lg p-4 ${styles.container} ${className} relative`}>
      <div
        className={`grid gap-6 items-center ${showIcon ? "grid-cols-[auto_1fr_auto]" : "grid-cols-[1fr_auto]"}`}
      >
        {showIcon && <Icon icon={iconName} className={`${styles.icon} text-xl`} />}
        <div className={styles.title}>
          <p className="font-medium">{title}</p>
          {description && (
            <div className={`text-sm ${styles.description} mt-1`}>
              {typeof description === "string" ? <p>{description}</p> : description}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setHide(true)}
        className={`absolute top-0 right-0 hover:opacity-70 transition-opacity p-1 rounded`}
        aria-label="Close notification"
      >
        <Icon icon="material-symbols:cancel" className="text-2xl" />
      </button>
    </div>
  );
}
